import XLSX from 'xlsx';
import XlsxPopulate from 'xlsx-populate';
import _ from 'lodash';
import { ExcelFileType, ExcelParseOption } from '../../../coreDomainsModule/translateExcel';
import {
  ALLOWED_FORECAST_FILE_NAMES,
  ALLOWED_DAILY_FORECAST_FILE_NAMES,
  ALLOWED_ARRIVAL_FILE_NAMES,
  ALLOWED_MASTER_DATA_FILE_NAMES,
  ALLOWED_SALES_RESULT_FILE_NAMES,
  ALLOWED_STOCK_FILE_NAMES,
  ALLOWED_ORDER_FILE_NAMES,
  ENGLISH_MONTH_TO_NUMBER,
} from '@/coreDomainsModule/constants';
import { ALL } from 'dns';

/**
 * CellObject内のKeyである.wか.vに値があるなら持ってくる関数
 * .vが生データを持っているので、それを持ってくる（エクセルの書式によって不具合が出るため）
 * 以下、CellObject部分のリファレンスです
 * https://www.npmjs.com/package/xlsx#:~:text=%3B%0A%20%20%7D%0A%7D-,Cell%20Object,-Cell%20objects%20are
 * @param cell セル情報
 * @returns rawValueを返す
 */
export const bringCellValue = (cell: any): string => {
  if (!cell) return ''; // セルが存在しない場合は空文字を返す
  return cell?.v?.toString() || ''; // `v` を優先して文字列に変換
};

/**
 * エクセルファイルを読み込む関数
 * @param e イベント
 */
export const eventParseFile = (e: Event) => {
  const files = (e.target as HTMLInputElement).files;
  if (!files) return;
  return files[0] as File;
};

/**
 * 年情報の配列内に年情報がひとつも入っていないかを確認する
 * 入っている場合、またはエクセルファイルを読んでいないが処理が行われる場合はtrueを返す
 * 無い場合にはfalseを返す
 * @param ymdArray
 * @returns 真偽値
 */
export const checkYearsInfoExists = (ymdArray: string[]): boolean => {
  // 内示エクセルなどで、パスの関係上エクセルファイル自体を読んでいない場合などを考慮
  // エクセル自体を読んでいなければ、ymdDataは空になる想定
  if (!ymdArray.length) return true;

  // 配列から年情報のみを取り出す
  const regex = /^\d+$/; //　全て数字の文字列のみにmatch
  const simpleYmdArray: string[] = ymdArray.filter((val) => {
    if (val && val.includes('年')) {
      val = val.split('年')[0];
    }
    return regex.test(val);
  });

  // ymdData(年情報が入っている配列)の中身が全て空だった場合の処理
  // この場合は、年情報が入っていないとする
  if (
    simpleYmdArray.every((element: string) => {
      return !element;
    })
  ) {
    return false;
  }
  // ひとつでも年情報が入っている場合は通す
  return true;
};

/**
 * Convert a month value to number, supporting both Japanese (1月, 2月) and English (Jan., February) formats
 * @param monthValue - The month string value
 * @returns The month number as string, or empty string if not recognized
 */
export const parseMonthToNumber = (monthValue: string): string => {
  // First try standard parseNumber for Japanese format (1月, 2月, etc.)
  const numericValue = monthValue.parseNumber();
  if (numericValue) {
    return numericValue.toString();
  }

  // Try English month format
  const trimmedLower = monthValue.trim().toLowerCase();
  const englishMonth = ENGLISH_MONTH_TO_NUMBER[trimmedLower];
  if (englishMonth !== undefined) {
    return englishMonth.toString();
  }

  return '';
};

/**
 *
 * @param keyVal
 * @param columnIndex
 * @param ymdArr
 * @param option
 */
export const fetchRowIndex = (keyVal: string, columnIndex: number, ymdArr: Array<string>, option?: ExcelParseOption): string => {
  // TODO:ここの分岐、在庫エクセルは以降の処理を行わないという意味で書いてあるがわかりにくいので修正する
  if (!option || option.fileName.includes('在庫_')) return keyVal;
  if (!option) return keyVal;
  const startAddress = XLSX.utils.decode_cell(option.separateYmd.startCell);
  // separateYmdの時だけ特別に対応する
  if (startAddress.c <= columnIndex) {
    // 単月との区分け処理
    // 内示エクセルでこの処理は通したくないため、回避している
    if (
      startAddress.c === columnIndex &&
      !keyVal &&
      !option.fileName.includes('内示_') &&
      !option.fileName.includes('日別内示_') &&
      !option.fileName.includes('Forecast_') &&
      !option.fileName.includes('DailyForecast_')
    ) {
      // 入荷、販売実績エクセル内、年月情報(202204みたいな)が入力されているセルの1つ下が空白の場合、その列を単月の「実績_数量」がある列とする
      // 単月のデータは「実績_数量_単月」を含むkeyを作成する
      return ymdArr[columnIndex - startAddress.c] + '_実績_数量_単月';
    }
    // Support both Japanese (1月) and English (Jan.) month formats
    const monthNumber = parseMonthToNumber(keyVal);
    return ymdArr[Math.floor((columnIndex - startAddress.c) / option.separateYmd.separateCount)] + '_' + monthNumber;
  }
  return keyVal;
};

/**
 * XlsxPopulate.fromDataAsyncを用いて、bufferをWorkSheetに変換する関数
 * TODO: 共通ファイルに移動する
 * @param blobOrFile ファイル情報
 */
export async function convertBufferToWorkSheet(blobOrFile: string | ArrayBuffer | null): Promise<string | ArrayBuffer | null> {
  // XlsxPopulate.fromDataAsync(data).then(async function (workbook) {
  const a = await XlsxPopulate.fromDataAsync(blobOrFile);
  // await new Promise<void>((resolve) => (XlsxPopulate.then = () => resolve()));

  return a;
}

/**
 * 転記の際に、オブジェクト生成による初期値の影響を吸収する関数
 * 何かを転記する際には、こちらの関数を通してください
 * @param amountValue 数量
 * @returns 文字列にして返す
 */
export const outputAmountValue = (amountValue?: number | string): string => {
  // 内示の初期値に対応：　初期値の際は転記元が空の為、空文字を返却する
  // 230730: 一旦FORECAST_INITIAL_VALUEのまま運用することとなったのでコメントアウトしてます
  // if (Number(amountValue) === FORECAST_INITIAL_VALUE) return '';
  // 他にも対応したいものがあったら、追記していく形で
  return String(amountValue);
};

/**
 *
 * @param str 文字列内にカンマがあった場合、エスケープした状態で文字列として返す
 * @returns string
 */
export const escapeStringComma = (str: string): string => {
  if (!str) return '';
  if (str.indexOf(',') > -1) {
    str = '"' + str + '"';
  }
  return str;
};

/**
 * 対象の整数データがfalsyな値かどうかをチェックする
 * falsyな値だった場合は0を返し、truthyな値はそのまま返す
 * @param value 確認したい整数データ
 * @returns 判定結果
 */
export const checkFalsyNumber = (value: number | undefined): number => {
  if (!value) {
    return 0;
  }
  return value;
};

//文字列から改行文字を削除する（csvで勝手に改行されないように）
/**
 *
 * @param str
 */
export const removeNewLineForText = (str: string) => {
  return str.replace(/\r?\n/g, '');
};

/**
 * セル番号の文字列から、行番号を取得する関数
 * @param cellAddress - セル番号の文字列 (例: "A1", "B2"など)
 */
export const getRowNumberFromAddress = (cellAddress: string): number => {
  const cell = XLSX.utils.decode_cell(cellAddress);
  return cell.r; // 行番号は0から始まる
};

/**
 * セル番号の文字列から、列番号を取得する関数
 * @param cellAddress - セル番号の文字列 (例: "A1", "B2"など)
 */
export const getColNumberFromAddress = (cellAddress: string): number => {
  const cell = XLSX.utils.decode_cell(cellAddress);
  return cell.c; // 行番号は0から始まる
};

/**
 * ファイル名として許可するものを、対応するexcelTypeに変換する
 * 対応するものがない場合はそのまま返す
 * NOTE: 英語ファイル名対応処理
 * @param fileName - ハイフン区切りのファイル名
 */
export const getExcelTypeFromFileName = (fileName: string): ExcelFileType | 'none' => {
  const normalizedFileName = fileName.replace(/\\/g, '/');
  const normalizedFnWithoutExt = normalizedFileName.replace(/\.[^/.]+$/, '');
  const normalizedFnLower = normalizedFnWithoutExt.toLowerCase();
  const normalizedFnSegmentsLower = normalizedFnWithoutExt.split(/[\\/_-]/).map((segment) => segment.toLowerCase());
  const hasAllowedName = (allowedFileNames: readonly string[]) =>
    allowedFileNames.some((allowedFileName) => {
      const allowedNameLower = allowedFileName.toLowerCase();
      return normalizedFnSegmentsLower.includes(allowedNameLower) || normalizedFnLower.startsWith(allowedNameLower);
    });

  // ファイル名に対応するexcelTypeを返す
  if (hasAllowedName(ALLOWED_MASTER_DATA_FILE_NAMES)) {
    return '在庫表';
  } else if (hasAllowedName(ALLOWED_DAILY_FORECAST_FILE_NAMES)) {
    return '日別内示';
  } else if (hasAllowedName(ALLOWED_FORECAST_FILE_NAMES)) {
    return '内示';
  } else if (hasAllowedName(ALLOWED_SALES_RESULT_FILE_NAMES)) {
    return '販売実績';
  } else if (hasAllowedName(ALLOWED_ARRIVAL_FILE_NAMES)) {
    return '入荷';
  } else if (hasAllowedName(ALLOWED_STOCK_FILE_NAMES)) {
    return '在庫';
  } else if (hasAllowedName(ALLOWED_ORDER_FILE_NAMES)) {
    return '発注取込';
  } else {
    // 対応するものがない場合、'none'を返す
    return 'none';
  }
};

/**
 * Excelの列名（A, B, Cなど）を数値（比較可能）に変換する関数
 * @param column - 列名
 */
export const columnToNumber = (column: string): number => {
  let number = 0;
  for (let i = 0; i < column.length; i++) {
    number *= 26;
    number += column.charCodeAt(i) - 'A'.charCodeAt(0) + 1;
  }
  return number;
};
